import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PrintPage from "@components/print/PrintElements/PrintPage";
import { useAppSelector } from "@/store/hooks";
import { calculateBuyInvoiceSummaryData, calculateExpenseSummaryData, generateReportTotal, ReportTotalDetail } from "@utils/DocumentStatus";
import ReportPage, { ReportDataProps } from "@components/dynamic/ReportPage";
import moment from "moment";

import { toNumberFormat } from "@utils/Utils";
import { CURRENCIES_CODES } from "@utils/Constants";
import { ExpenseApi } from "@/api/ExpenseApi";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";
import { ProviderApi } from "@/api/ProviderApi";
import { BuyInvoiceApi } from "@/api/BuyInvoiceApi";

type ExpenseByPeriodReportPrintProps = {
  excelExport?: boolean
}
const ExpenseByPeriodReportPrint = (props: ExpenseByPeriodReportPrintProps) => {
  const { excelExport = false } = props
  const params = useParams()
  const { providerId, startDate = "", endDate = "", autoPrint = false } = params;
  const suscription = useAppSelector((state) => { return state.suscription.suscription || {} });
  const [reportData, setReportData] = useState<ReportDataProps[]>([]);
  const [reportTotals, setReportTotals] = useState<ReportTotalDetail[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    import("@printerVariation/generic.scss");
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      PrintPageDataLoadedPubSub.publish({ dataLoaded: false })
      loadReportData()
    }
  }, [startDate, endDate]);

  const loadReportData = async () => {
    setIsLoading(true);
    try {
      let filters = {}
      if (providerId && providerId != "-2") {// -2 all the propviders
        filters = {
          providerId: { eq: providerId }
        }
      }

      const buyInvoicefilter = {
        ...filters,
        buyInvoiceType: { eq: "Expense" }
      }

      const providers = await ProviderApi.optionsList();
      const { items: expenses } = await ExpenseApi.fetchAllByPeriod(startDate, endDate, filters, "", 10000)
      const { items: buyInvoices } = await BuyInvoiceApi.fetchAllByPeriod(startDate, endDate, buyInvoicefilter, "", 10000)

      const currenciesOnReport = new Set<string>();

      const expensesData = expenses.map(d => {
        const date = moment(d.fecha, "YYYY-MM-DD")
        const formatDate = date.format("DD-MM-YYYY")
        const currencyId = Number(d.currency.currency_id || 1);
        const { subtotal, descuento, impuestos, total } = calculateExpenseSummaryData(d);
        const tieneNote = d.expenseNotes && d.expenseNotes?.length > 0
        currenciesOnReport.add(CURRENCIES_CODES[currencyId]);

        let data: ReportDataProps = {
          values: [
            formatDate,
            `${d.sequence}`,
            `${d.nombre}`,
            `${d.expenseCategory?.title || ""}`,
            tieneNote ? "Si" : "No",
            `${toNumberFormat(subtotal)}`,
            `${toNumberFormat(descuento)}`,
            `${toNumberFormat(impuestos)}`,
            `${toNumberFormat(total)}`,
            CURRENCIES_CODES[currencyId],
            `${d.exchange_rate}`,
            `${d.reference_number || ""}`,
          ]
        }
        return data
      })

      const buyInvoicesData = buyInvoices.map(d => {
        const date = moment(d.fecha, "YYYY-MM-DD")
        const formatDate = date.format("DD-MM-YYYY")
        const currencyId = Number(d.currency.currency_id || 1);
        const { subtotal, descuento, impuestos, total } = calculateBuyInvoiceSummaryData(d);
        // const tieneNote = d.providerNotes && d.providerNotes?.length > 0
        const tieneNote = false
        currenciesOnReport.add(CURRENCIES_CODES[currencyId]);

        const providerName = providers[`${d.providerId}`] || ""

        let data: ReportDataProps = {
          values: [
            formatDate,
            `FEC-${d.numero}`,
            providerName,
            "",
            tieneNote ? "Si" : "No",
            `${toNumberFormat(subtotal)}`,
            `${toNumberFormat(descuento)}`,
            `${toNumberFormat(impuestos)}`,
            `${toNumberFormat(total)}`,
            CURRENCIES_CODES[currencyId],
            `${d.exchange_rate}`,
            `${d.reference_number || ""}`,
          ]
        }
        return data
      })

      let reportData = [
        ...expensesData,
        ...buyInvoicesData
      ]

      let sortFunction = (a?: ReportDataProps, b?: ReportDataProps) => {
        if (!a || !b) return 0; // seguridad por si alguno es undefined
        const dataA = moment(a.values[0], "DD-MM-YYYY");// Fecha
        const dataB = moment(b.values[0], "DD-MM-YYYY");// fecha
        const dateCompare = dataA.unix() - dataB.unix();
        if (dateCompare != 0) {
          return dateCompare
        } else {
          const nameA = a.values[2]
          const nameB = a.values[2]

          const nameCompare = nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
          return nameCompare;
        }
      };
      reportData = reportData.sort(sortFunction)

      const reportTotal = generateReportTotal(reportData, [
        {
          dataColumnIndex: 5,
          title: "Subtotal Gastos"
        },
        {
          dataColumnIndex: 6,
          title: "Total Descuento"
        },
        {
          dataColumnIndex: 7,
          title: "Total IVA"
        },
        {
          dataColumnIndex: 8,
          title: "Total Gastos"
        },
      ], currenciesOnReport, 9);

      setReportData(reportData)
      setReportTotals(reportTotal)
    } finally {
      setIsLoading(false);
      PrintPageDataLoadedPubSub.publish({ dataLoaded: true })
    }
  }

  return (
    <div>
      <PrintPage
        autoPrint={autoPrint === "1"}
        onReadyDebounce={2000}
      >
        <ReportPage
          loading={isLoading}
          excelExport={excelExport}
          suscription={suscription}
          reportConfig={{
            title: "Reporte Gastos",
            date: `${moment().format()}`,
          }}
          reportTotals={reportTotals}
          reportData={reportData}
          reportTitles={[
            { label: "Fecha", width: "10%" },
            { label: "# Factura", width: "10%" },
            { label: "A Nombre de", width: "20%" },
            { label: "Categoria", width: "10%" },
            { label: "Tiene Nota", width: "5%" },
            { label: "Subtotal", width: "8%", isNumeric: true },
            { label: "Descuento", width: "8%", isNumeric: true },
            { label: "Iva", width: "8%", isNumeric: true },
            { label: "Total", width: "8%", isNumeric: true },
            { label: "Moneda", width: "5%" },
            { label: "Tipo de Cambio", width: "5%", isNumeric: true },
            { label: `${suscription.reference_label || "# Referencia"}`, width: "0%", hideFromPdf: true },
          ]}
        />
      </PrintPage>

    </div>
  );
}

export default ExpenseByPeriodReportPrint;
